import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * Bandito: sepete ekleme onay ekranı (snippets/sepete-eklendi.liquid).
 *
 * Masaüstünde ortada modal, mobilde alttan yukarı çekmece — ikisi de aynı
 * `<dialog>`, farkı yalnızca CSS.
 *
 * Akış: product-form.js sepete ekleyince `cart:lines:update` (action: 'add')
 * yayınlıyor. Biz eklenen varyantı `event.lines[0].merchandiseId` ile öğrenip
 * `sections/sepete-eklendi.liquid` bölümünü taze çekiyoruz; bölüm sepetteki tüm
 * satırları basıyor, eşleşmeyeni burada atıyoruz. Fiyat biçimleme Liquid'de
 * kalsın diye bu yol seçildi — istemcide para formatı kodu yok.
 *
 * Öneriler ("Bunlar da yakışır") modal açıldıktan SONRA ayrı istekle geliyor;
 * onay ekranı öneri isteğini beklemiyor.
 *
 * Hata olursa hiçbir şey gösterilmiyor: ürün sepete girdi, onay ekranının
 * açılamaması alışverişi bozmamalı.
 */

const ONERI_ADEDI = 6;

/**
 * Verilen dialog'u, sahibi bir DialogComponent ise onun API'siyle kapatır
 * (kapanış animasyonu ve scroll kilidi orada çözülüyor).
 * @param {HTMLDialogElement} dialog
 */
function dialogKapat(dialog) {
  for (let el = dialog.parentElement; el; el = el.parentElement) {
    if (typeof (/** @type {any} */ (el).closeDialog) === 'function') {
      /** @type {any} */ (el).closeDialog();
      return;
    }
  }
  dialog.close();
}

/** @param {string} html */
function ayristir(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} icerik
 * @property {HTMLElement} oneriler
 *
 * @extends {DialogComponent}
 */
class SepeteEklendiComponent extends DialogComponent {
  requiredRefs = ['dialog', 'icerik', 'oneriler'];

  /** Aynı anda tek istek: hızlı üst üste eklemede son eklenen kazansın. */
  #istekSayaci = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#eklemeyiYakala);
    this.addEventListener('click', this.#devamaBasildi);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#eklemeyiYakala);
    this.removeEventListener('click', this.#devamaBasildi);
  }

  /** "Alışverişe Devam Et" — gövde sonradan enjekte edildiği için delegasyonla. */
  #devamaBasildi = (/** @type {MouseEvent} */ event) => {
    const hedef = event.target;
    if (hedef instanceof Element && hedef.closest('[data-rol="devam"]')) this.closeDialog();
  };

  /** @param {any} event `CartLinesUpdateEvent` */
  #eklemeyiYakala = (event) => {
    if (event.action !== 'add') return;

    const variantId = String(event.lines?.[0]?.merchandiseId ?? '');

    // Ekleme ile onay ekranı arasında iki istek var (sepet tazeleme + bölüm);
    // o boşlukta buton ölü görünmesin diye hemen beklemeye alınıyor.
    const buton = event.target instanceof Element ? event.target.querySelector('add-to-cart-component') : null;
    buton?.setAttribute('aria-busy', 'true');

    // Ekleme bir modal içinden geldiyse (quick-add) önce o kapansın: iki modal
    // üst üste binmesin ve odak doğru yere dönsün.
    const kaynak =
      event.target instanceof Element
        ? /** @type {HTMLDialogElement | null} */ (event.target.closest('dialog:modal'))
        : null;

    event.promise
      ?.then((/** @type {any} */ sonuc) => {
        if (sonuc?.detail?.didError) return;
        return this.#ac(variantId, kaynak);
      })
      .catch(() => {})
      .finally(() => buton?.removeAttribute('aria-busy'));
  };

  /**
   * @param {string} variantId - Eklenen varyant.
   * @param {HTMLDialogElement | null} kaynak - Eklemenin geldiği açık modal, varsa.
   */
  async #ac(variantId, kaynak) {
    const sayac = ++this.#istekSayaci;

    /** @type {Element | null} */
    let govde = null;
    try {
      // cache: 'no-store' şart: bu adres her seferinde aynı ("/?section_id=…"),
      // içeriği ise sepete göre değişiyor. Tarayıcı ilk yanıtı (çoğu zaman sepet
      // boşken alınan) saklarsa sonraki eklemelerde eski gövde dönüyor, eşleşen
      // satır bulunamıyor ve onay ekranı hiç açılmıyordu — gizli sekmede
      // çalışıp normal sekmede çalışmamasının sebebi buydu.
      const yanit = await fetch(String(this.dataset.bolumUrl), { cache: 'no-store' });
      if (!yanit.ok) return;
      govde = ayristir(await yanit.text()).querySelector('.se__govde');
    } catch {
      return;
    }

    if (!govde || sayac !== this.#istekSayaci) return;

    // Bölüm sepetteki her satırı basıyor; eklenen dışındakiler gitsin.
    const satirlar = /** @type {HTMLElement[]} */ ([...govde.querySelectorAll('.se__satir')]);
    const eslesen = satirlar.find((satir) => satir.dataset.variantId === variantId) ?? satirlar[0];
    if (!eslesen) return; // Sepet boş görünüyor — gösterecek bir şey yok.
    for (const satir of satirlar) {
      if (satir !== eslesen) satir.remove();
    }

    this.refs.icerik.replaceChildren(govde);
    this.refs.oneriler.replaceChildren();

    if (kaynak?.open) {
      await new Promise((cozul) => {
        kaynak.addEventListener('close', () => cozul(undefined), { once: true });
        dialogKapat(kaynak);
        // Kapanış animasyonu takılırsa onay ekranını rehin bırakmayalım.
        setTimeout(() => cozul(undefined), 600);
      });
      if (sayac !== this.#istekSayaci) return;
    }

    this.showDialog();
    this.#onerileriYukle(eslesen.dataset.productId, sayac);
  }

  /**
   * @param {string | undefined} productId
   * @param {number} sayac - Bu isteği başlatan açılış; arada yeni ekleme olduysa yaz-geç.
   */
  async #onerileriYukle(productId, sayac) {
    if (!productId) return;

    const url = new URL(String(this.dataset.oneriUrl), window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(ONERI_ADEDI));
    url.searchParams.set('section_id', 'sepete-eklendi-oneriler');

    try {
      const yanit = await fetch(url);
      if (!yanit.ok) return;
      const blok = ayristir(await yanit.text()).querySelector('.se__oneri-blok');
      if (blok && sayac === this.#istekSayaci && this.refs.dialog.open) {
        this.refs.oneriler.replaceChildren(blok);
      }
    } catch {
      // Öneri gelmezse onay ekranı önerisiz kalır, sorun değil.
    }
  }
}

if (!customElements.get('sepete-eklendi-component')) {
  customElements.define('sepete-eklendi-component', SepeteEklendiComponent);
}
